/**
 * @fileoverview Zod schemas for Indirect Costs DTOs and paginated responses.
 * @module features/indirect-costs/schemas/indirectCostsSchema
 */

import { z } from 'zod'

/**
 * IndirectCostEntryDTO as defined by OpenAPI.
 */
export const indirectCostEntrySchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  reportingAmount: z.number(),
  originalAmount: z.number().nullable().optional(),
  reportingCurrency: z.string(),
  originalCurrency: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  effectiveFrom: z.string(), // date (YYYY-MM-DD)
  effectiveTo: z.string(), // date (YYYY-MM-DD) exclusive
  category: z.enum(['OPEX', 'DA']),
  subcategoryUuid: z.string().uuid(),
  subcategoryDisplayName: z.string(),
  subcategorySlug: z.string(),
  subcategorySystem: z.boolean(),
  createdAt: z.string(), // date
  updatedAt: z.string(), // date
})
export type IndirectCostEntryDTO = z.infer<typeof indirectCostEntrySchema>

/**
 * Pageable sort metadata schema used in responses.
 */
export const sortMetaSchema = z.object({
  empty: z.boolean(),
  sorted: z.boolean(),
  unsorted: z.boolean(),
})

/**
 * Simple page metadata used by PagedModel responses.
 */
export const pageMetadataSchema = z
  .object({
    size: z.number(),
    number: z.number(),
    totalElements: z.number(),
    totalPages: z.number(),
  })
  .passthrough()
export type PageMetadata = z.infer<typeof pageMetadataSchema>

/**
 * Pageable information schema as returned by backend.
 */
export const pageableSchema = z.object({
  pageNumber: z.number(),
  pageSize: z.number(),
  sort: sortMetaSchema,
  offset: z.number(),
  paged: z.boolean(),
  unpaged: z.boolean(),
})

/**
 * Page response schema for IndirectCostEntryDTO.
 */
export const pageIndirectCostResponseSchema = z
  .union([
    z.object({
      content: z.array(indirectCostEntrySchema).default([]),
      page: pageMetadataSchema,
      sort: sortMetaSchema.optional(),
    }),
    z.object({
      content: z.array(indirectCostEntrySchema).default([]),
      pageable: pageableSchema.optional(),
      totalPages: z.number().optional(),
      totalElements: z.number().optional(),
      last: z.boolean().optional(),
      size: z.number().optional(),
      number: z.number().optional(),
      sort: sortMetaSchema.optional(),
      numberOfElements: z.number().optional(),
      first: z.boolean().optional(),
      empty: z.boolean().optional(),
    }),
  ])
  .transform(data => {
    if ('page' in data) {
      const meta = data.page
      const numberOfElements = data.content.length
      return {
        content: data.content,
        page: meta,
        pageable: undefined,
        sort: data.sort,
        size: meta.size,
        number: meta.number,
        totalElements: meta.totalElements,
        totalPages: meta.totalPages,
        last: meta.number + 1 >= meta.totalPages,
        first: meta.number === 0,
        numberOfElements,
        empty: numberOfElements === 0,
      }
    }

    const meta = {
      size: data.size ?? data.pageable?.pageSize ?? data.content.length,
      number: data.number ?? data.pageable?.pageNumber ?? 0,
      totalElements: data.totalElements ?? data.content.length,
      totalPages: data.totalPages ?? 1,
    }
    const numberOfElements = data.numberOfElements ?? data.content.length

    return {
      content: data.content,
      page: meta,
      pageable: data.pageable,
      sort: data.sort,
      size: meta.size,
      number: meta.number,
      totalElements: meta.totalElements,
      totalPages: meta.totalPages,
      last: data.last ?? meta.number + 1 >= meta.totalPages,
      first: data.first ?? meta.number === 0,
      numberOfElements,
      empty: data.empty ?? numberOfElements === 0,
    }
  })
export type PageIndirectCostResponse = z.infer<typeof pageIndirectCostResponseSchema>

/**
 * Schema describing an indirect cost subcategory.
 */
export const indirectCostSubcategorySchema = z.object({
  id: z.number(),
  uuid: z.string().uuid(),
  displayName: z.string(),
  slug: z.string(),
  category: z.enum(['OPEX', 'DA']),
  system: z.boolean(),
  autoCreateMonthly: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type IndirectCostSubcategoryDTO = z.infer<typeof indirectCostSubcategorySchema>

/**
 * Paginated response schema for indirect cost subcategories.
 */
export const pageIndirectCostSubcategoryResponseSchema = z
  .union([
    z.object({
      content: z.array(indirectCostSubcategorySchema).default([]),
      page: pageMetadataSchema,
      sort: sortMetaSchema.optional(),
    }),
    z.object({
      content: z.array(indirectCostSubcategorySchema).default([]),
      pageable: pageableSchema.optional(),
      totalPages: z.number().optional(),
      totalElements: z.number().optional(),
      last: z.boolean().optional(),
      size: z.number().optional(),
      number: z.number().optional(),
      sort: sortMetaSchema.optional(),
      numberOfElements: z.number().optional(),
      first: z.boolean().optional(),
      empty: z.boolean().optional(),
    }),
  ])
  .transform(data => {
    if ('page' in data) {
      const meta = data.page
      const numberOfElements = data.content.length
      return {
        content: data.content,
        page: meta,
        pageable: undefined,
        sort: data.sort,
        size: meta.size,
        number: meta.number,
        totalElements: meta.totalElements,
        totalPages: meta.totalPages,
        last: meta.number + 1 >= meta.totalPages,
        first: meta.number === 0,
        numberOfElements,
        empty: numberOfElements === 0,
      }
    }

    const meta = {
      size: data.size ?? data.pageable?.pageSize ?? data.content.length,
      number: data.number ?? data.pageable?.pageNumber ?? 0,
      totalElements: data.totalElements ?? data.content.length,
      totalPages: data.totalPages ?? 1,
    }
    const numberOfElements = data.numberOfElements ?? data.content.length

    return {
      content: data.content,
      page: meta,
      pageable: data.pageable,
      sort: data.sort,
      size: meta.size,
      number: meta.number,
      totalElements: meta.totalElements,
      totalPages: meta.totalPages,
      last: data.last ?? meta.number + 1 >= meta.totalPages,
      first: data.first ?? meta.number === 0,
      numberOfElements,
      empty: data.empty ?? numberOfElements === 0,
    }
  })
export type PageIndirectCostSubcategoryResponse = z.infer<
  typeof pageIndirectCostSubcategoryResponseSchema
>

export const indirectCostLinkPaymentSchema = z
  .object({
    paymentUuid: z.string(),
    linkedAt: z.string().nullable().optional(),
    txnDate: z.string(),
    title: z.string(),
    counterpartyName: z.string().nullable().optional(),
    reportingCurrency: z.string(),
    reportingAmount: z.number(),
    reportingNetAmount: z.number().nullable().optional(),
    reportingVatAmount: z.number().nullable().optional(),
    originalCurrency: z.string().nullable().optional(),
    originalAmount: z.number().nullable().optional(),
    originalNetAmount: z.number().nullable().optional(),
    originalVatAmount: z.number().nullable().optional(),
    direction: z.string(),
  })
  .passthrough()
export type IndirectCostLinkPaymentDTO = z.infer<typeof indirectCostLinkPaymentSchema>

export const indirectCostLinkWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
})
export type IndirectCostLinkWarningDTO = z.infer<typeof indirectCostLinkWarningSchema>

export const indirectCostLinksResponseSchema = z.object({
  costUuid: z.string(),
  reportingTotal: z.number(),
  reportingCurrency: z.string(),
  effectiveFrom: z.string().nullable().optional(),
  effectiveTo: z.string().nullable().optional(),
  category: z.enum(['OPEX', 'DA']).nullable().optional(),
  subcategoryUuid: z.string().uuid().nullable().optional(),
  subcategoryDisplayName: z.string().nullable().optional(),
  subcategorySlug: z.string().nullable().optional(),
  subcategorySystem: z.boolean().nullable().optional(),
  description: z.string().nullable().optional(),
  payments: z.array(indirectCostLinkPaymentSchema).default([]),
  warnings: z.array(indirectCostLinkWarningSchema).optional().default([]),
})
export type IndirectCostLinksResponse = z.infer<typeof indirectCostLinksResponseSchema>

/**
 * Schema for indirect cost within payment links response.
 */
export const paymentLinkedCostSchema = z.object({
  costUuid: z.string().uuid(),
  effectiveFrom: z.string(),
  effectiveTo: z.string(),
  category: z.enum(['OPEX', 'DA']),
  subcategoryUuid: z.string().uuid(),
  subcategoryDisplayName: z.string(),
  subcategorySlug: z.string(),
  subcategorySystem: z.boolean(),
  description: z.string().nullable().optional(),
  reportingAmount: z.number(),
  reportingCurrency: z.string().nullable().optional(),
  linkedAt: z.string().datetime().nullable().optional(),
  contributionAmount: z.number(),
})
export type PaymentLinkedCostDTO = z.infer<typeof paymentLinkedCostSchema>

/**
 * Schema for payment links response showing all indirect costs linked to a specific payment.
 */
export const paymentLinksResponseSchema = z.object({
  paymentUuid: z.string(),
  txnDate: z.string(),
  title: z.string(),
  reportingAmount: z.number(),
  allocatedTotal: z.number(),
  indirectCosts: z.array(paymentLinkedCostSchema).default([]),
  warnings: z.array(indirectCostLinkWarningSchema).default([]),
})
export type PaymentLinksResponse = z.infer<typeof paymentLinksResponseSchema>
